import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { OdataService } from '@app/Services/o-data.service';
import { createChildGridOptions } from '@app/Shared/grid/data-explorer-grid-options';
import { iParsedEntity } from '@app/Shared/interfaces/iEntityParsed';
import { AppState } from '@app/Store';
import { dashboardFeatureSelectors } from '@app/Store/features/dashboard.feature';
import { select, Store } from '@ngrx/store';
import { ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-sources-tab',
    templateUrl: './sources-tab.component.html',
    styleUrls: ['./sources-tab.component.scss'],
})
export class SourcesTabComponent implements OnInit, OnDestroy  {
    public gridOptions: GridOptions;
    public columnDefs: ColDef[] = [];
    public sourcesRowData: any[] = [];
    public selectedEntity: iParsedEntity | null = null;
    public noRecordSelected = true;
    public loading = false;
    public errorMessage: string | null = null;

    private subscription = new Subscription();
    private gridApi: any;

    constructor(
        private store: Store<AppState>,
        private cdr: ChangeDetectorRef,
        private odataService: OdataService,
    ) {
        this.gridOptions = createChildGridOptions(() => false);
        this.gridOptions.suppressNoRowsOverlay = false;
        this.gridOptions.localeText = { noRowsToShow: 'No sources for this master record' };
    }

    ngOnInit(): void {
        this.subscription.add(
            combineLatest([
                this.store.pipe(
                    select(dashboardFeatureSelectors.selectSelectedEntity),
                    distinctUntilChanged((prev, curr) => prev?.entityName === curr?.entityName),
                ),
                this.store.pipe(
                    select(dashboardFeatureSelectors.selectSelectedRecords),
                    distinctUntilChanged((prev, curr) => {
                        if (prev?.length !== curr?.length) return false;
                        if (prev?.length === 0 && curr?.length === 0) return true;
                        return prev?.[0]?.data === curr?.[0]?.data;
                    }),
                ),
            ])
                .pipe(
                    distinctUntilChanged((prev, curr) => {
                        const [prevEntity, prevRecords] = prev;
                        const [currEntity, currRecords] = curr;
                        if (prevEntity?.entityName !== currEntity?.entityName) return false;
                        if (prevRecords?.length !== currRecords?.length) return false;
                        if (prevRecords?.length === 0 && currRecords?.length === 0) return true;
                        const prevKey = prevRecords?.[0]?.data?.[prevEntity?.key];
                        const currKey = currRecords?.[0]?.data?.[currEntity?.key];
                        return prevKey === currKey;
                    }),
                )
                .subscribe(([entity, selectedRecords]) => {
                    if (entity && selectedRecords?.length > 0) {
                        this.selectedEntity = entity;
                        this.noRecordSelected = false;
                        const masterKey = selectedRecords[0].data[entity.key];
                        this.fetchSources(entity, masterKey);
                    } else {
                        this.noRecordSelected = true;
                        this.selectedEntity = null;
                        this.sourcesRowData = [];
                        this.columnDefs = [];
                        this.errorMessage = null;
                        this.updateGridData();
                        this.cdr.detectChanges();
                    }
                }),
        );
    }
    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private fetchSources(entity: iParsedEntity, masterKey: number | string): void {
        this.loading = true;
        this.errorMessage = null;
        this.cdr.detectChanges();

        this.odataService.getEntitySources(entity.entityName, entity.key, masterKey).subscribe({
            next: (response) => {
                this.loading = false;
                const value = response?.value;
                if (!value || value.length === 0) {
                    this.sourcesRowData = [];
                    this.columnDefs = [];
                } else {
                    const sourcesCollectionKey = entity.entityName.charAt(0).toUpperCase() + entity.entityName.slice(1) + 'Srcs';
                    const clientSrcs: any[] = value[0][sourcesCollectionKey];
                    if (!clientSrcs || !Array.isArray(clientSrcs)) {
                        this.sourcesRowData = [];
                        this.columnDefs = [];
                    } else {
                        const flattened = this.flattenSources(clientSrcs);
                        this.sourcesRowData = flattened.rows;
                        this.columnDefs = flattened.columnDefs;
                    }
                }
                this.updateGridData();
                this.cdr.detectChanges();
            },
            error: (err) => {
                this.loading = false;
                this.errorMessage = err?.message || 'Failed to load sources';
                this.sourcesRowData = [];
                this.columnDefs = [];
                this.updateGridData();
                this.cdr.detectChanges();
            },
        });
    }

    /**
     * Flatten ClientSrcs items: each item has Src nested; flatten to one row per item with Src fields prefixed.
     */
    private flattenSources(clientSrcs: any[]): { rows: any[]; columnDefs: ColDef[] } {
        const rows = clientSrcs.map((item) => {
            const flat: any = { ...item };
            if (item.Src && typeof item.Src === 'object') {
                Object.keys(item.Src).forEach((k) => {
                    flat['Src_' + k] = item.Src[k];
                });
                delete flat.Src;
            }
            return flat;
        });

        const allKeys = new Set<string>();
        rows.forEach((r) => Object.keys(r).forEach((k) => allKeys.add(k)));
        const columnDefs: ColDef[] = Array.from(allKeys)
            .sort()
            .map((field) => ({
                field,
                headerName: field
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (s) => s.toUpperCase())
                    .trim(),
                sortable: true,
                resizable: true,
                flex: 1,
                minWidth: 200,
            }));

        return { rows, columnDefs };
    }

    private updateGridData(): void {
        if (this.gridApi) {
            this.gridApi.setColumnDefs(this.columnDefs);
            this.gridApi.setRowData(this.sourcesRowData || []);
        }
    }

    onGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;
        this.gridApi.setColumnDefs(this.columnDefs);
        this.gridApi.setRowData(this.sourcesRowData || []);
    }
}
